import styled from "styled-components";
import { useMemo, useState } from "react";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import useApi from "../../hooks/useApi";
import { formatCurrency } from "../../Funcoes/formatCurrency";
import { useToast } from "../../contexts/ToastContext";

const Title = styled.h2`
    margin: 0 0 4px 0;
    font-size: 18px;
    color: var(--ctrl-text, #333);
`;

const Subtitle = styled.p`
    margin: 0 0 16px 0;
    font-size: 14px;
    color: var(--ctrl-text-muted, #555);
`;

const AlertaConsistencia = styled.div`
    margin: 0 0 16px 0;
    padding: 10px 12px;
    border-radius: 6px;
    border: 1px solid ${({ $status }) => {
        if ($status === "DIVERGENCIA") return "#f5c2c7";
        if ($status === "SEM CONDICAO") return "#ffe69c";
        return "#ffecb5";
    }};
    background: ${({ $status }) => {
        if ($status === "DIVERGENCIA") return "#f8d7da";
        if ($status === "SEM CONDICAO") return "#fff3cd";
        return "#fff8e1";
    }};
    color: var(--ctrl-text, #333);
    font-size: 13px;
`;

const AlertaTitulo = styled.div`
    font-weight: 600;
    margin-bottom: 4px;
`;

const AlertaLista = styled.ul`
    margin: 0;
    padding-left: 18px;
`;

const Lista = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 55dvh;
    overflow: auto;
`;

const ParcelaLinha = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px;
    border: 1px solid var(--ctrl-border, #ddd);
    border-radius: 8px;
`;

const ParcelaInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
`;

const ParcelaTitulo = styled.span`
    font-size: 14px;
    font-weight: bold;
    color: var(--ctrl-text, #333);
`;

const ParcelaDetalhe = styled.span`
    font-size: 12px;
    color: var(--ctrl-text-muted, #666);
`;

const InputData = styled.input`
    padding: 8px;
    box-sizing: border-box;
    border: 1px solid var(--ctrl-border, #ccc);
    border-radius: 4px;
    font-size: 14px;
    &:focus {
        border-color: #007bff;
        outline: none;
        box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
    }
`;

const EmptyText = styled.p`
    margin: 0;
    font-size: 14px;
    color: var(--ctrl-text-muted, #666);
`;

const BotoesWrapper = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
`;

const BotaoGerar = styled.button`
    padding: 8px 16px;
    background-color: #007bff;
    color: #fff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    &:hover {
        background-color: #0056b3;
    }
    &:disabled {
        background-color: #9bc4ec;
        cursor: not-allowed;
    }
`;

const datasIniciais = (parcelas) =>
    (parcelas || []).reduce((acc, p) => {
        if (p.item && p.data_prevista) {
            acc[p.item] = p.data_prevista;
        }
        return acc;
    }, {});

const labelStatusConsistencia = (status) => {
    if (status === "DIVERGENCIA") return "Divergência na condição de pagamento";
    if (status === "SEM CONDICAO") return "Pedido sem condição de pagamento";
    if (status === "ALERTA") return "Alerta na condição de pagamento";
    return "Atenção";
};

const ModalParcelas = ({ fecharModal, pedido, valorTotal, parcelas, consistencia }) => {
    const api = useApi();
    const { notifyError } = useToast();
    const [pdfLoading, setPdfLoading] = useState(false);
    const [datas, setDatas] = useState(() => datasIniciais(parcelas));

    const todasPreenchidas = useMemo(
        () => parcelas.length > 0 && parcelas.every((p) => datas[p.item]),
        [parcelas, datas]
    );

    const handleDataChange = (item, value) => {
        setDatas((prev) => ({ ...prev, [item]: value }));
    };

    const gerarPDF = async () => {
        if (!todasPreenchidas || pdfLoading) return;

        setPdfLoading(true);
        try {
            const body = {
                filial: pedido.FILIAL,
                num_pedido: pedido.NUM_PEDIDO,
                parcelas: parcelas.map((p) => ({
                    item: p.item,
                    data: datas[p.item],
                    valor: p.valor,
                    rateio: p.rateio
                }))
            };

            const response = await api.post(
                "Painel/API/Controladoria/GeneratePDF/index.php",
                body,
                { responseType: "blob" }
            );

            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `Pedido_de_Compra_${pedido.NUM_PEDIDO}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            fecharModal();
        } catch (e) {
            notifyError(e?.response?.data?.message || "Erro ao gerar o PDF do pedido.");
        } finally {
            setPdfLoading(false);
        }
    };

    const statusConsistencia = consistencia?.status;
    const mostrarAlerta = statusConsistencia && statusConsistencia !== "OK";
    const motivos = consistencia?.motivos || [];

    return (
        <Overlay state={true}>
            <Modal size="md" header close={fecharModal}>
                <Title>Parcelas do Pagamento</Title>
                <Subtitle>
                    Pedido: <strong>{pedido?.NUM_PEDIDO}</strong> | Valor Total:{" "}
                    <strong>R$ {formatCurrency(valorTotal)}</strong>
                </Subtitle>

                {mostrarAlerta && (
                    <AlertaConsistencia $status={statusConsistencia}>
                        <AlertaTitulo>{labelStatusConsistencia(statusConsistencia)}</AlertaTitulo>
                        {motivos.length > 0 ? (
                            <AlertaLista>
                                {motivos.map((motivo, idx) => (
                                    <li key={`${idx}-${motivo}`}>{motivo}</li>
                                ))}
                            </AlertaLista>
                        ) : null}
                    </AlertaConsistencia>
                )}

                {parcelas.length === 0 ? (
                    <EmptyText>Nenhuma parcela encontrada para a condição de pagamento deste pedido.</EmptyText>
                ) : (
                    <Lista>
                        {parcelas.map((p) => (
                            <ParcelaLinha key={p.item}>
                                <ParcelaInfo>
                                    <ParcelaTitulo>Parcela {p.item}</ParcelaTitulo>
                                    <ParcelaDetalhe>
                                        {p.rateio}% &middot; R$ {formatCurrency(p.valor)}
                                    </ParcelaDetalhe>
                                </ParcelaInfo>
                                <InputData
                                    type="date"
                                    value={datas[p.item] || ""}
                                    onChange={(e) => handleDataChange(p.item, e.target.value)}
                                />
                            </ParcelaLinha>
                        ))}
                    </Lista>
                )}

                <BotoesWrapper>
                    <BotaoGerar onClick={gerarPDF} disabled={!todasPreenchidas || pdfLoading}>
                        {pdfLoading ? "Gerando PDF..." : "Imprimir Pedido de Compra"}
                    </BotaoGerar>
                </BotoesWrapper>
            </Modal>
        </Overlay>
    );
};

export default ModalParcelas;
